import { useGetProjectTestSessionsInfiniteQuery } from '../hooks/bug-management/useProjectFindings'
import { testSessionStatus } from '../lib/testSessionStatus'
import { useCallback, useMemo, useState } from 'react'
import { RefreshCw, Search } from 'lucide-react'
import { } from '../services/projectDataApi'
import { pullRequestSearchText } from '../lib/githubTestRun'
import { pausedStatus, type ProjectPause } from '../lib/projectPause'
import TestRunTable from './TestRunTable'
import InfiniteScrollTrigger from './InfiniteScrollTrigger'
import './TestRunListTab.css'

interface TestRunListTabProps {
  projectId: string
  /** A paused project cannot have an actively executing run, even if its stored session is live. */
  pause?: ProjectPause | null
  /** The unified project page owns this list through its project-keyed RTK Query cache. */
  managed?: boolean
}

// The Test Runs tab lists TEST SESSIONS — a user-facing "test run" is one round of QA work, with its
// exploration(s), journey runs, and bugs nested inside it (see the run detail page).
export default function TestRunListTab({ projectId, pause = null }: TestRunListTabProps) {
  const [search, setSearch] = useState('')
  const testSessionsQuery = useGetProjectTestSessionsInfiniteQuery(
    { projectId, pageSize: 50 },
    {
      pollingInterval: 60_000,
      skipPollingIfUnfocused: true,
      refetchCachedPages: false,
    },
  )
  const sessionPages = testSessionsQuery.data?.pages ?? []
  const items = useMemo(() => {
    const seen = new Set<string>()
    return sessionPages.flatMap(page => page.items).filter(session => {
      if (seen.has(session.id)) return false
      seen.add(session.id)
      return true
    })
  }, [sessionPages])
  const testRunOrder = useMemo(() => {
    const seen = new Set<string>()
    return sessionPages.flatMap(page => page.testRunOrder).filter(item => {
      const key = item.kind === 'session' ? `session:${item.session_id}` : `github-pr:${item.version_id}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [sessionPages])
  const loadMore = useCallback(() => {
    if (testSessionsQuery.hasNextPage && !testSessionsQuery.isFetchingNextPage) void testSessionsQuery.fetchNextPage()
  }, [testSessionsQuery])
  // Each page arrives with the collapsed-row summaries for its own GitHub PRs, so the rows this tab is
  // showing are exactly the PRs it has fetched — scrolling in costs one page, not another pass over the
  // project's whole PR history. A row's expanded contents load when it is opened (the run detail page).
  const loadedGithubPrs = useMemo(() => {
    const seen = new Set<string>()
    return sessionPages.flatMap(page => page.githubPrs).filter(pr => {
      if (seen.has(pr.version_id)) return false
      seen.add(pr.version_id)
      return true
    })
  }, [sessionPages])

  // Client-side filter over the run's title, status, journey names, and GitHub source metadata — what
  // a reader can actually see in the table. The table itself also renders unmatched PRs as temporary
  // frontend-only rows until the data model grows a direct session-to-PR relationship.
  const visibleRuns = useMemo(() => {
    const q = search.trim().toLowerCase()
    const prByVersion = new Map(loadedGithubPrs.map((pr) => [pr.version_id, pr]))
    const matches = (session: (typeof items)[number]) => {
      if (!q) return true
      const pr = session.version_id ? prByVersion.get(session.version_id) : undefined
      const displayedStatus = pausedStatus(testSessionStatus(session), pause)
      return session.title.toLowerCase().includes(q) ||
        displayedStatus.toLowerCase().includes(q) ||
        (session.prLabel ?? '').toLowerCase().includes(q) ||
        session.journeyRuns.some(r => (r.journey_name ?? r.goal ?? '').toLowerCase().includes(q)) ||
        !!pr && pullRequestSearchText(pr).includes(q)
    }
    return items.filter(matches)
  }, [items, loadedGithubPrs, pause, search])

  const visibleGithubPrs = useMemo(() => {
    const q = search.trim().toLowerCase()
    const visibleSessionVersions = new Set(visibleRuns.map((session) => session.version_id).filter((id): id is string => !!id))
    return loadedGithubPrs.filter((pr) => visibleSessionVersions.has(pr.version_id) || !q || pullRequestSearchText(pr).includes(q))
  }, [loadedGithubPrs, search, visibleRuns])

  if (testSessionsQuery.isLoading) {
    return (
      <div className="test-run-list test-run-list--loading" data-testid="test-run-list-loading" role="status" aria-label="Loading test runs">
        <div className="test-run-list__title-row">
          <h2 className="project-tab-title">Test Runs</h2>
          <span className="test-run-list__skeleton test-run-list__skeleton--action" />
        </div>
        <div className="test-run-list__skeleton-table" aria-hidden>
          <span className="test-run-list__skeleton test-run-list__skeleton--header" />
          {Array.from({ length: 6 }).map((_, index) => (
            <span key={index} className="test-run-list__skeleton test-run-list__skeleton--row" />
          ))}
        </div>
      </div>
    )
  }

  if (testSessionsQuery.isError && items.length === 0) {
    return (
      <div className="test-run-list">
        <div className="test-run-list__title-row">
          <h2 className="project-tab-title">Test Runs</h2>
        </div>
        <div className="test-run-list__error" role="alert" data-testid="test-run-list-error">
          <p>Failed to load test runs</p>
          <button onClick={() => void testSessionsQuery.refetch({ refetchCachedPages: false })} data-testid="test-run-list-retry" data-analytics-event="test-run-list-retry">
            <RefreshCw size={14} aria-hidden="true" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  const visibleLegacyOrGithubRuns = visibleGithubPrs.some((pr) => !items.some((session) => session.version_id === pr.version_id))

  if (items.length === 0 && !visibleLegacyOrGithubRuns) {
    return (
      <div className="test-run-list">
        <div className="test-run-list__title-row">
          <h2 className="project-tab-title">Test Runs</h2>
        </div>
        <div className="test-run-list__empty-container">
          <div className="test-run-list__empty" data-testid="test-run-list-empty">No test runs yet.</div>
        </div>
      </div>
    )
  }

  return (
    <div className="test-run-list" data-testid="test-run-list">
      <div className="test-run-list__title-row">
        <h2 className="project-tab-title">Test Runs</h2>
        <div className="test-run-list__header">
          <label className="test-run-list__search">
            <Search size={14} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search test runs"
              data-testid="test-run-search"
            />
          </label>
        </div>
      </div>
      {visibleRuns.length > 0 ? (
        <TestRunTable projectId={projectId} sessions={visibleRuns} githubPrs={visibleGithubPrs} testRunOrder={testRunOrder} pause={pause} />
      ) : visibleLegacyOrGithubRuns ? (
        <TestRunTable projectId={projectId} sessions={[]} githubPrs={visibleGithubPrs} testRunOrder={testRunOrder} pause={pause} />
      ) : (
        <div className="test-run-list__empty" data-testid="test-run-search-empty">
          No test runs match “{search.trim()}”.
        </div>
      )}
      <InfiniteScrollTrigger
        hasMore={testSessionsQuery.hasNextPage}
        isLoading={testSessionsQuery.isFetchingNextPage}
        error={testSessionsQuery.isError ? 'load-error' : null}
        onLoadMore={loadMore}
        onRetry={() => void testSessionsQuery.refetch({ refetchCachedPages: false })}
        label="test runs"
      />
    </div>
  )
}
